import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { Input, Button } from '../ui'; // Assuming Input and Button accept translated strings/props
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Therapist, BillingAddress } from '../../types/firestoreTypes';

// Define the shape of the form data
type BillingFormData = Pick<
  Therapist,
  'billingName' | 'billingAddress' | 'vatNumber'
>;

const BillingSettingsTab: React.FC = () => {
  const { t } = useTranslation(); // Get the t function
  const { user, therapist } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  // Store translation keys for success/error messages
  const [successMessageKey, setSuccessMessageKey] = useState<string | null>(
    null
  );
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [formData, setFormData] = useState<BillingFormData>({
    billingName: '',
    billingAddress: {
      // Initialize address object
      street: '',
      city: '',
      postalCode: '',
      country: '',
    },
    vatNumber: '',
  });

  useEffect(() => {
    if (therapist) {
      setFormData({
        billingName: therapist.billingName || '',
        // Ensure billingAddress is always an object, even if null/undefined in Firestore
        billingAddress: therapist.billingAddress || {
          street: '',
          city: '',
          postalCode: '',
          country: '',
        },
        vatNumber: therapist.vatNumber || '',
      });
    }
  }, [therapist]);

  // Handles changes for both top-level fields and nested address fields
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => {
      const newData = structuredClone(prev); // Deep clone to handle nested state safely
      if (field.startsWith('billingAddress.')) {
        const addressField = field.split('.')[1] as keyof BillingAddress;
        // Ensure billingAddress exists before assigning
        if (!newData.billingAddress) {
          newData.billingAddress = {};
        }
        (newData.billingAddress as any)[addressField] = value;
      } else {
        (newData as any)[field] = value;
      }
      return newData;
    });
    // Clear errors/success when user starts typing
    setErrorKey(null);
    setSuccessMessageKey(null);
  };

  const handleSaveBilling = async () => {
    if (!user) return;

    setIsLoading(true);
    setErrorKey(null);
    setSuccessMessageKey(null);

    try {
      const therapistRef = doc(db, 'therapists', user.uid);
      // Prepare data for Firestore update, ensuring address is included
      const updateData: Partial<Therapist> = {
        billingName: formData.billingName,
        // Ensure address fields are trimmed or handled as needed before saving
        billingAddress: {
          street: formData.billingAddress?.street?.trim() || '',
          city: formData.billingAddress?.city?.trim() || '',
          postalCode: formData.billingAddress?.postalCode?.trim() || '',
          country: formData.billingAddress?.country?.trim() || '',
        },
        vatNumber: formData.vatNumber?.trim() || '',
      };

      await updateDoc(therapistRef, updateData);
      setSuccessMessageKey('settings.billing.success.update'); // Use key
    } catch (err) {
      console.error('Error updating billing information:', err);
      setErrorKey('settings.billing.errors.updateFailed'); // Use key
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-[#EAECF0] p-6 space-y-6">
      <h2 className="text-lg font-semibold text-[#101828]">
        {t('settings.billing.heading')} {/* Translate heading */}
      </h2>

      {/* Display translated success message */}
      {successMessageKey && (
        <div className="p-4 bg-green-50 text-green-700 rounded-lg">
          {t(successMessageKey)}
        </div>
      )}

      {/* Display translated error message */}
      {errorKey && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg">
          {t(errorKey)}
        </div>
      )}

      <div className="space-y-6">
        <Input
          label={t('settings.billing.form.billingName.label')} // Translate label
          value={formData.billingName}
          onChange={(e) => handleInputChange('billingName', e.target.value)}
          placeholder={t('settings.billing.form.billingName.placeholder')} // Translate placeholder
        />

        <div className="space-y-4">
          <h3 className="text-sm font-medium text-[#344054]">
            {t('settings.billing.form.address.label')}{' '}
            {/* Translate sub-heading */}
          </h3>
          <Input
            label={t('settings.billing.form.address.street.label')} // Translate label
            value={formData.billingAddress?.street || ''}
            onChange={(e) =>
              handleInputChange('billingAddress.street', e.target.value)
            }
            placeholder={t('settings.billing.form.address.street.placeholder')} // Translate placeholder
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {' '}
            {/* Responsive grid */}
            <Input
              label={t('settings.billing.form.address.city.label')} // Translate label
              value={formData.billingAddress?.city || ''}
              onChange={(e) =>
                handleInputChange('billingAddress.city', e.target.value)
              }
              placeholder={t('settings.billing.form.address.city.placeholder')} // Translate placeholder
            />
            <Input
              label={t('settings.billing.form.address.postalCode.label')} // Translate label
              value={formData.billingAddress?.postalCode || ''}
              onChange={(e) =>
                handleInputChange('billingAddress.postalCode', e.target.value)
              }
              placeholder={t(
                'settings.billing.form.address.postalCode.placeholder'
              )} // Translate placeholder
            />
          </div>
          <Input
            label={t('settings.billing.form.address.country.label')} // Translate label
            value={formData.billingAddress?.country || ''}
            onChange={(e) =>
              handleInputChange('billingAddress.country', e.target.value)
            }
            placeholder={t('settings.billing.form.address.country.placeholder')} // Translate placeholder
          />
        </div>

        <Input
          label={t('settings.billing.form.vatNumber.label')} // Translate label
          value={formData.vatNumber || ''}
          onChange={(e) => handleInputChange('vatNumber', e.target.value)}
          placeholder={t('settings.billing.form.vatNumber.placeholder')} // Translate placeholder
        />

        <Button
          variant="secondary"
          onClick={handleSaveBilling}
          isLoading={isLoading}
          disabled={isLoading}
        >
          {t('settings.billing.buttons.save')} {/* Translate button text */}
        </Button>
      </div>
    </div>
  );
};

export default BillingSettingsTab;
